/**
 * Purchase Request Redux helpers (HPRMS Phase A).
 * Boot + Sync list GET; open = Redux; hole-fill GET-by-id; upsert after successful write.
 */

import {
  getPurchaseRequests,
  getPurchaseRequestById,
  mapPurchaseRequestSnapshot,
} from "../apis/purchaseJourney.api";
import { PURCHASE_REQUESTS_PAGE_SIZE } from "./purchaseRequestsConstants";

const getStore = () => require("../index").default;

export const findPurchaseRequestInState = (prsState, id) => {
  if (id == null || String(id).trim().toLowerCase() === "null") return null;
  const key = String(id);
  const list = prsState?.purchaseRequests || [];
  return list.find((pr) => String(pr?.POID ?? pr?.id ?? "") === key) || null;
};

export const purchaseRequestsPresentInStore = (prsState) => {
  const state = prsState || getStore().getState()?.purchaseRequestsState;
  if (!state) return false;
  if (state.purchaseRequestsLoaded) return true;
  return (state.purchaseRequests || []).length > 0;
};

export const isPurchaseRequestDetailUsable = (pr) =>
  Boolean(pr && Array.isArray(pr.lineItems) && pr.lineItems.length > 0);

export const upsertPurchaseRequestInStore = (pr) => {
  if (!pr) return;
  const id = pr.POID || pr.id;
  if (!id) return;
  getStore().dispatch({
    type: "UPSERT_PURCHASE_REQUEST",
    payload: {
      ...pr,
      POID: id,
      id: pr.id || id,
    },
  });
};

export const applyPurchaseRequestsToStore = (list) => {
  getStore().dispatch({
    type: "SET_PURCHASE_REQUESTS",
    payload: Array.isArray(list) ? list : [],
  });
};

/** Replace PR list from API → Redux. Use for Sync / force refresh. */
export const refreshPurchaseRequestsInStore = async ({
  start = 0,
  size = PURCHASE_REQUESTS_PAGE_SIZE,
  status = null,
  context = "Loading Purchase Requests",
  silent = true,
} = {}) => {
  const list = await getPurchaseRequests({
    start,
    size,
    status: status || undefined,
    silent,
    hideLoader: silent,
    context,
  });
  applyPurchaseRequestsToStore(list);
  return list;
};

/** Ensure PRs in Redux. Skips network when already loaded unless force. */
export const ensurePurchaseRequestsInStore = async ({
  force = false,
  silent = true,
  context = "Loading Purchase Requests",
} = {}) => {
  if (!force && purchaseRequestsPresentInStore()) {
    return getStore().getState()?.purchaseRequestsState?.purchaseRequests || [];
  }
  return refreshPurchaseRequestsInStore({ silent, context });
};

/**
 * Redux-first PR by id. Network GET header+lines only on miss/thin.
 */
export const resolvePurchaseRequestById = async (id, { forceNetwork = false } = {}) => {
  const key = String(id ?? "").trim();
  if (!key || key.toLowerCase() === "null") return null;

  const store = getStore();
  if (!forceNetwork) {
    const fromStore = findPurchaseRequestInState(
      store.getState()?.purchaseRequestsState,
      key
    );
    if (isPurchaseRequestDetailUsable(fromStore)) return fromStore;
  }

  const full = await getPurchaseRequestById(key);
  if (!full) return null;
  const stamped = { ...full, __detailFetched: true };
  upsertPurchaseRequestInStore(stamped);
  return stamped;
};

export const stampPurchaseRequestWrite = (id, data, lineItems = []) => {
  const snapshot = mapPurchaseRequestSnapshot(id, data, lineItems);
  upsertPurchaseRequestInStore({ ...snapshot, __detailFetched: true });
  return snapshot;
};
